import moment from 'moment';
import { memo, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getDashboardData } from '../../../api/dashboard';
import { useSetLoading } from '../../../common/hooks/appearance';
import { futureDuration, reportTypes } from '../../../constants/reports';
import { routes } from '../../../constants/routes';
import { dateFormat, fetchDashboardFilterData } from '../../../utils/constant';
import { apiErrorHandler, getStartDateEndDate } from '../../../utils/handlers';
import ARAgingGraph from '../graph-cards/AR-Aging';
import IndividualDashboardLayout from '../IndividualDashboardLayout';
import { DASHBOARD_COLORS, DURATION, PROJECTIONS_STRUCTURE } from '../utils/dashboardConstants';

function ProjectionsDashboard() {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ ...PROJECTIONS_STRUCTURE });
  const [date, setDate] = useState(getStartDateEndDate(futureDuration[1].value));
  const [duration, setDuration] = useState(futureDuration[1]);
  const [contractNumber, setContractNumber] = useState('');
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);
  const filtersReady = filterData?.entity?.length > 0;
  const durationNote = `The above graph represents future stats of ${
    duration?.value !== 'custom'
      ? duration?.label
      : `${moment(date.startDate).format('MM/DD/YYYY')} - ${moment(date.endDate).format(
          'MM/DD/YYYY',
        )} custom`
  } duration`;

  const getDashboardHome = async () => {
    setLoading(true);
    try {
      const dataAvailable = fetchDashboardFilterData(filterData);
      const reqParams = {
        ...dataAvailable,
        opiOrName: contractNumber,
        startDate: moment(date.startDate).format(dateFormat.YEAR_MONTH_DAY),
        endDate: moment(date.endDate).format(dateFormat.YEAR_MONTH_DAY),
        timezone: moment.tz.guess(),
      };
      const { data: responseData } = await getDashboardData('dashboard', 'projections', reqParams);
      setData(responseData.data.items);
    } catch (error) {
      setLoading(false);
      apiErrorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filtersReady) getDashboardHome();
  }, [filtersReady, filterData, date, contractNumber]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const redirectionHandler = (query) => {
    const updatedQuery = { ...query };
    updatedQuery.reportDate = date.endDate;
    updatedQuery.startDate = date.startDate;
    updatedQuery.endDate = date.endDate;
    updatedQuery.duration = duration;
    updatedQuery.searchText = contractNumber;
    history.push({ pathname: query.pathname, state: updatedQuery });
  };

  const dateChangeHandler = (changedDate, changedDuration) => {
    setDate(changedDate);
    setDuration(changedDuration);
  };
  const searchHandler = (searchedValue) => setContractNumber(searchedValue);

  const getReportTypeValues = (reportType) => {
    return reportTypes.find((report) => report.value === reportType);
  };

  return (
    <IndividualDashboardLayout
      durationType={DURATION.FUTURE}
      dateChangeHandler={dateChangeHandler}
      searchHandler={searchHandler}
    >
      <div className="dashboard-row-container">
        <ARAgingGraph
          id="future-paid-in-full-widget"
          data={{
            graphType: 'bar',
            title: 'Future Paid in Full',
            tooltip:
              'This chart will show the amount of payments which Cypress Lawn is going to receive in future for making contracts paid in full',
            graphColor: DASHBOARD_COLORS.FUTURE_PAID_IN_FULL,
            graphTitle: {
              y: 'AMOUNT',
              x: 'ENTITY',
            },
            showDataLabels: true,
            impNote: durationNote,
            series: [
              {
                name: 'Amount',
                type: 'column',
                data: data.futureDeed.values,
              },
            ],
            xLabels: data.futureDeed.labels,
            redirection: {
              enabled: true,
              path: routes.REPORTS,
              filterType: 'reportType',
              redirect: redirectionHandler,
              values: getReportTypeValues('futurePaidinFullReport'),
            },
          }}
        />
        <ARAgingGraph
          id="projections-cashflow-widget"
          data={{
            graphType: 'bar',
            title: 'Projected Cashflow',
            tooltip:
              'This chart will show the amount of payments which Cypress Lawn is going to receive in future for all the contracts',
            graphColor: DASHBOARD_COLORS.PROJECTED_CASHFLOW,
            graphTitle: {
              x: 'COLLECTION BUCKETS',
              y: 'AMOUNT',
            },
            showDataLabels: true,
            impNote: durationNote,
            series: [
              {
                name: 'Amount',
                type: 'column',
                data: data.projectedCashflow.values,
              },
            ],
            xLabels: data.projectedCashflow.labels,
            redirection: {
              enabled: false,
              path: routes.REPORTS,
              filterType: 'reportType',
              redirect: redirectionHandler,
              values: getReportTypeValues('futurePaidinFullReport'),
            },
          }}
        />
      </div>
    </IndividualDashboardLayout>
  );
}

export default memo(ProjectionsDashboard);
